import React, { useEffect, useState } from 'react';
import { Assets, Colors, FontFamily, PrimaryButton } from 'utils/imports.utils';
import { ButtonSize, ButtonTypes } from 'utils/interface.utils';
import './project_modal.component.scss';
import { Models } from 'utils/imports.utils';
import { useDispatch } from 'react-redux';
import { setProject } from '../../store/reducers/projectSlice';
import { toastifyError, useSetState } from 'utils/functions.utils';
import { STRINGS } from 'constants/strings.constant';

interface IProjectModal {
  // projectId: string;
  closeModal: () => void;
  onCreate: (project: { project_id: string; project_name: string }) => void;
}

const ProjectModal: React.FC<IProjectModal> = ({
  // projectId,
  closeModal,
  onCreate,
}) => {
  const [state, setState] = useSetState({
    isDisabled: true,
    projectName: '',
    isLoading: false,
    errorMessage: '',
  });

  const dispatch = useDispatch();

  const handleSubmit = async () => {
    setState({ isLoading: true, errorMessage: '' });
    if (!state.projectName.trim()) {
      toastifyError('Project name is required!');
      setState({ isLoading: false });
      return;
    }
    try {
      const response: any = await Models.auth.createProject({
        project_name: state.projectName.trim(),
      });

      if (response && response.data) {
        const newProject = {
          project_id: response.data._id,
          project_name: response.data.project_name,
        };

        setState({ projectName: '', isLoading: false });
        dispatch(setProject(newProject)); // store in Redux
        onCreate(newProject); //Trigger parent handler
      } else {
        toastifyError('Failed to create project.');
        setState({ isLoading: false });
      }
    } catch (err: any) {
      const message =
        typeof err === 'string'
          ? err
          : err?.message || 'Error saving project';
      console.error(err);
      if (message === 'Project already exists') {
        setState({
          isLoading: false,
          errorMessage: STRINGS.project.duplicateName(state.projectName.trim()),
        });
        return;
      }
      toastifyError(message);
      setState({ isLoading: false });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !state.isDisabled) handleSubmit();
  }

  useEffect(() => {
    const disabled =
      !state.projectName.trim() || state.isLoading || !!state.errorMessage;
    setState({ isDisabled: disabled });
  }, [state.projectName, state.isLoading, state.errorMessage]);

  return (
    <div
      className="project_modal"
      onClick={(e) => e.target === e.currentTarget && closeModal()}>
      <div className="project_modal_container">
        {/* Header */}
        <div className="project_modal_header">
          <div>
            <div className="project_modal_title">{STRINGS.project.createDialogTitle}</div>
            <div className="project_modal_subtitle">{STRINGS.project.createDialogHint}</div>
          </div>
          <div onClick={closeModal} className="project_modal_close">
            <img
              className="project_modal_close_icon"
              src={Assets.close_black}
              alt="Close"
            />
          </div>
        </div>

        {/* Body */}
        <div className="project_modal_body">
          <label className="title" htmlFor="project_modal_name">{STRINGS.project.nameLabel}</label>
          <input
            id="project_modal_name"
            type="text"
            value={state.projectName}
            onChange={(e) =>
              setState({ projectName: e.target.value, errorMessage: '' })
            }
            onKeyDown={handleKeyDown}
            placeholder={STRINGS.project.namePlaceholder}
            className={`project_modal_input${state.errorMessage ? ' has_error' : ''}`}
            aria-invalid={!!state.errorMessage}
            aria-describedby="project_modal_message"
            autoFocus
          />
          {state.errorMessage ? (
            <div
              id="project_modal_message"
              className="project_modal_error"
              role="alert">
              <svg
                className="project_modal_error_icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 4.5v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="8" cy="11.2" r="0.9" fill="currentColor" />
              </svg>
              <span>{state.errorMessage}</span>
            </div>
          ) : (
            <div id="project_modal_message" className="project_modal_help">
              {STRINGS.project.nameHelp}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="project_modal_footer">
          <div className="cancel">
            <PrimaryButton
              type={ButtonTypes.OUTLINED}
              size={ButtonSize.MEDIUM}
              fontFamily={FontFamily.medium}
              text="Cancel"
              onClick={closeModal}
              style={{ borderColor: Colors.ink, borderRadius: '999px' }}
            />
          </div>
          <div className="send">
            <PrimaryButton
              onClick={handleSubmit}
              type={
                state.isDisabled
                  ? ButtonTypes.DISABLED
                  : ButtonTypes.PRIMARY_GRADIENT
              }
              size={ButtonSize.MEDIUM}
              fontFamily={FontFamily.medium}
              text={state.isLoading ? 'Saving...' : 'Create project'}
              disabled={state.isDisabled}
              activity={state.isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
